import { useMutation } from "@apollo/client/react";
import { Base64 } from "js-base64";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { ConfigInput, ConfigType, Context, HooksInput } from "types/generated";
import { fileToBase64 } from "utils/file";
import { PhaseName, adaptCommandsToFields } from "utils/hooks";

import { CONTEXT_ANALYTICS_VERSION } from "../constants";
import { CREATE_CONTEXT_V2 } from "../gql";
import { ContextFormFields } from "../types";

type SaveContextCallback = (formData: ContextFormFields) => Promise<void>;

type UseSaveContext = [SaveContextCallback, boolean];

export const useSaveContext = (step?: string): UseSaveContext => {
  const { reportSuccess, onError } = useTypedContext(FlashContext);
  const navigate = useNavigate();

  const trackAnalyticsEvent = useAnalytics({
    defaultCallbackTrackProperties: { location: step, version: CONTEXT_ANALYTICS_VERSION },
  });

  const [saveContextMutation, { loading: isSavingContext }] = useMutation<{
    contextCreateV2: Context;
  }>(CREATE_CONTEXT_V2);

  const saveContext = useCallback<SaveContextCallback>(
    async (formData) => {
      try {
        const stackAttachments = formData.attachedProjectIds.map((id) => ({
          stack: id,
          priority: 0,
        }));

        const configAttachments: Omit<ConfigInput, "fileMode">[] = [];

        for (const variable of formData.attachedVariables) {
          configAttachments.push({
            description: variable.description,
            id: variable.variableName,
            type: ConfigType.EnvironmentVariable,
            value: variable.value,
            writeOnly: variable.isSecret,
          });
        }

        for (const mountedFile of formData.attachedMountedFiles) {
          const { file, content, description, path, isSecret } = mountedFile;
          let value = "";

          if (content) {
            value = Base64.encode(content);
          } else if (file) {
            const base64 = await fileToBase64(file as File);
            const base64File = base64 ? base64.toString().split(",")[1] : "";
            value = base64File;
          } else {
            continue;
          }

          configAttachments.push({
            description,
            id: path,
            type: ConfigType.FileMount,
            value: value,
            writeOnly: isSecret,
          });
        }

        let hooks = {} as HooksInput;

        for (const [phase, commands] of Object.entries(formData.hooks)) {
          hooks = {
            ...hooks,
            ...adaptCommandsToFields(commands, phase as PhaseName),
          };
        }

        const contextInput = {
          description: formData.description,
          labels: formData.labels.map((item) => item.value),
          name: formData.contextName,
          space: formData.space,
          stackAttachments,
          configAttachments,
          hooks,
        };

        const { data } = await saveContextMutation({
          variables: {
            input: contextInput,
          },
        });

        const contextId = data?.contextCreateV2?.id;

        if (contextId) {
          reportSuccess({
            message: `Context "${data.contextCreateV2.name}" was successfully created`,
          });
          trackAnalyticsEvent("Wizard - Create Context Clicked");
          navigate(`/context/${contextId}`);
        }
      } catch (e) {
        onError(e);
      }
    },
    [saveContextMutation, navigate, onError, reportSuccess, trackAnalyticsEvent]
  );

  return [saveContext, isSavingContext];
};
